/**
 * Hooks for group quote settings (hotel info, discount tiers, email template)
 *
 * Replaces localStorage-based settings with server-side storage.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { DEFAULT_EMAIL_TEMPLATE } from "../../pages/groups/constants";
import type {
  GroupQuoteSettingsResponse,
  HotelInfo,
  DiscountTier,
  EmailTemplate,
} from "../../pages/groups/types";

export interface GroupQuoteSettingsData {
  hotelInfo: HotelInfo | null;
  discountTiers: DiscountTier[];
  emailTemplate: EmailTemplate;
}

export interface UpdateGroupQuoteSettingsInput {
  hotelInfo?: Partial<HotelInfo>;
  discountTiers?: DiscountTier[];
  emailTemplate?: Partial<EmailTemplate>;
}

/**
 * Fetch group quote settings from the server
 */
export function useGroupQuoteSettings() {
  return useQuery<GroupQuoteSettingsResponse, Error, GroupQuoteSettingsData>({
    queryKey: ["group-quote-settings"],
    queryFn: async () => {
      try {
        return await api.groupQuotes.getSettings();
      } catch (error: unknown) {
        // Treat 404 as "no settings yet" - return empty structure
        if (
          error &&
          typeof error === "object" &&
          "response" in error &&
          (error as { response?: { status?: number } }).response?.status === 404
        ) {
          return {
            success: true,
            data: {
              hotelInfo: null,
              discountTiers: [],
              emailTemplate: DEFAULT_EMAIL_TEMPLATE,
            },
          };
        }
        throw error;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (response) => response.data,
  });
}

/**
 * Update group quote settings on the server
 */
export function useUpdateGroupQuoteSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateGroupQuoteSettingsInput) =>
      api.groupQuotes.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-quote-settings"] });
    },
  });
}
